import {
  BREADCRUMBS_POSITION_OPTIONS,
  CONTAINER_OPTIONS,
  CONTENT_LAYOUT_OPTIONS,
  HEADING_ALIGNMENT_OPTIONS,
  MODULE_FILL_TYPE_OPTIONS,
  MODULE_FLOW_DIRECTION_OPTIONS,
  TITLE_SIZE_OPTIONS,
  TITLE_TYPE_OPTIONS,
} from '@/common/constants';
import { useModuleStyles } from '@/common/hooks';
import { BaseModuleProps } from '@/common/types';
import { HeadingBlock, HeadingBlockProps } from '@/components/blocks/HeadingBlock/HeadingBlock';
import { ImageBlock, ImageBlockProps } from '@/components/blocks/ImageBlock/ImageBlock';
import { Breadcrumbs, BreadcrumbsProps } from '@/components/Breadcrumbs/Breadcrumbs';
import classNames from 'classnames';
import React from 'react';

import styles from './StandardCTA.module.scss';

export interface StandardCTAProps extends BaseModuleProps {
  breadcrumbs?: BreadcrumbsProps;
  /** Image for the standard CTA */
  image?: ImageBlockProps;
}

/**
 * Standard CTA module component
 * @param props - The props for the Standard CTA module
 * @returns The Standard CTA module component
 */
export const StandardCTA: React.FC<StandardCTAProps> = ({
  anchorTag = undefined,
  container = CONTAINER_OPTIONS.CONTAINED,
  fillType = MODULE_FILL_TYPE_OPTIONS.BASE,
  gradientFillToken,
  breadcrumbs,
  layout = CONTENT_LAYOUT_OPTIONS.CONTENT_TRAILING,
  flowDirection = MODULE_FLOW_DIRECTION_OPTIONS.ROW,
  eyebrow,
  title,
  titleSize = TITLE_SIZE_OPTIONS.LARGE,
  titleType = TITLE_TYPE_OPTIONS.H2,
  bodyRichTextAsHTML,
  alignment = HEADING_ALIGNMENT_OPTIONS.START,
  ctas,
  caption,
  image,
  isFirstModule,
}) => {
  const headingConfig: HeadingBlockProps = {
    eyebrow,
    title,
    titleSize,
    titleType,
    bodyRichTextAsHTML,
    alignment,
    ctas,
    caption,
  };

  // Show breadcrumbs only if position is page-top
  const showBreadcrumbs =
    isFirstModule && breadcrumbs && breadcrumbs.position === BREADCRUMBS_POSITION_OPTIONS.PAGE_TOP;
  const breadcrumbStyles = showBreadcrumbs ? styles.with_breadcrumb : '';
  const isImageFill = fillType === MODULE_FILL_TYPE_OPTIONS.IMAGE;

  // Use the common module styles hook
  const { sectionStyles, moduleBaseStyles } = useModuleStyles({
    container,
    fillType,
    gradientFillToken,
    layout,
    flowDirection,
    moduleClassName: 'module-standard-cta',
    styles,
    isFirstModule,
  });
  const sectionClassNames = classNames(sectionStyles, breadcrumbStyles);

  return (
    <section className={sectionClassNames} id={anchorTag}>
      {showBreadcrumbs && (
        <Breadcrumbs
          {...breadcrumbs}
          className={styles.module_breadcrumbs}
          showInverse={isImageFill}
        />
      )}
      <div className={moduleBaseStyles}>
        <HeadingBlock {...headingConfig} />
        {image && (
          <ImageBlock
            src={image.src}
            alt={image.alt}
            aspectRatio={image.aspectRatio}
            className={styles['module-content-swap']}
            responsive={{
              compactWidth: 648,
              desktopWidth: 648,
              sizes: '(max-width: 600px) 100vw, 648px',
            }}
          />
        )}
      </div>
    </section>
  );
};
